import {Game, ILocation} from "@game/game";
import {Vector2} from "@utility/vector2";
import {selectMap} from "@redux/selectors/map";
import {connect} from "react-redux";
import {CSSProperties, ReactElement, useCallback, useEffect, useState} from "react";
import {UserPlayer} from "@game/player/userPlayer";
import {TileBiome, TileBlock, TileLetter} from "@components/map/SquareView";
import {GameEvent} from "@game/gameEvent";
import {WordOutline} from "@components/map/WordOutline";
import {Bounds2} from "@utility/bounds2";
import {TILE_SIZE, LETTER_THEME} from "@components/core/theme";
import {Tile} from "@game/map/tile";
import {Letter} from "@game/map/letter";
import {FloodEvent} from "@components/map/FloodEvent";
import {ContourLines, CONTOUR_RECENTER_X, CONTOUR_RECENTER_Y} from "@components/map/ContourLines";
import {PlayerLabels} from "@components/map/OtherPlayerLabels";
import {LeaderboardLabels} from "@components/map/LeaderboardLabels";
import {IShovelAnim} from "@components/map/MapController";

const TILE_BORDER = new Vector2(2, 2); // extra margin on top of the screen-size buffer

const DIG_KEYFRAMES = `
@keyframes dig-animation {
    0%   { transform: rotate(0deg);   }
    15%  { transform: rotate(-8deg);  }
    35%  { transform: rotate(8deg);   }
    55%  { transform: rotate(-4deg);  }
    75%  { transform: rotate(2deg);   }
    100% { transform: rotate(0deg);   }
}
`;

const layerStyle: CSSProperties = { position: "absolute", top: 0, left: 0, pointerEvents: "none" };

const styles = {
    canvas: { position: "absolute", top: 0, left: 0, width: "100%", height: "100%" } as CSSProperties,
    players: {}
};

interface IMapEvent {
    type: string
    tiles: ILocation[]
}

interface IMapView {
    bounds: Bounds2
    game: Game
    hiddenTilePos?: Vector2
    mapOffset: Vector2
    shovelAnim: IShovelAnim | null
    shovelling: boolean
    userPlayer: UserPlayer
    zoom: number
}

function MapView({bounds, game, hiddenTilePos, mapOffset, shovelAnim, shovelling, userPlayer, zoom}: IMapView) {
    const [dragging, setDragging] = useState<Letter>();
    const [firstTile, setFirstTile] = useState<Tile>();
    const [dirty, setDirty] = useState(false);
    const [event, setEvent] = useState<IMapEvent>();
    const [placedTiles, setPlacedTiles] = useState(userPlayer.hand.placed.length);
    const [placedLetters, setPlacedLetters] = useState("");
    const [scored, setScored] = useState(false);
    const [biomeTiles, setBiomeTiles] =
        useState<ReactElement[]>([]);
    const [blockTiles, setBlockTiles] =
        useState<ReactElement[]>([]);
    const [letterTiles_, setLetterTiles_] =
        useState<ReactElement[]>([]);
    const [contourOrigin, setContourOrigin] = useState<Vector2 | null>(null);
    const [visibleTiles, setVisibleTiles] = useState<Tile[]>([]);
    const [personalLabelTiles, setPersonalLabelTiles] = useState<Set<string>>(new Set());
    const handleEventComplete = () => setEvent(undefined);

    useEffect(() => {
        setDirty(true);
    }, [shovelling]);
    useEffect(() => {
        setDirty(true);
    }, [hiddenTilePos]);
    useEffect(() => {
        const handleEvent = (event: GameEvent) => {
            if (event.type === "WordScoresUpdated") {
                setScored(true);
            } else if (event.type === "PlayerScored") {
                setScored(false);
                setEvent(event.properties.event);
            }
            else if (event.type === "ShovelledTile") {
                setDirty(true);
            }
        };

        game.events.delegates.add(handleEvent);
        return () => game.events.delegates.remove(handleEvent);
    }, [game]);
    useEffect(() => {
        const handleEvent = (event: GameEvent) => {
            if (event.type === "DragPlacedLetter" || event.type === "DropLetter" ||
                event.type === "ResetPlacedLetters" || event.type === "PlayerScored") {
                setScored(false);
            }
        };

        userPlayer.hand.events.delegates.add(handleEvent);
        return () => userPlayer.hand.events.delegates.remove(handleEvent);
    }, [userPlayer]);
    useEffect(() => {
        const updateTiles = () => {
            const zoomedBounds = bounds.multiply(1 / zoom);
            const offsetBounds = zoomedBounds.minus(mapOffset);

            if (offsetBounds.width() !== 0) {
                const tileBounds = offsetBounds.grow(zoomedBounds.size().multiply(0.5).plus(TILE_BORDER.multiply(TILE_SIZE)))
                    .expand(Vector2.one.divide(TILE_SIZE));
                const nearbyTiles = Object.values(userPlayer.nearbyRegions)
                    .map(region => region.tiles).flat()
                    .filter(tile => tileBounds.contains(tile.position));
                const nearbyLetters = nearbyTiles.map(tile => tile.letter?.letter || " ").join("");

                if (dirty || firstTile !== nearbyTiles[0] || nearbyLetters != placedLetters ||
                    placedTiles !== userPlayer.hand.placed.length || userPlayer.hand.dragging !== dragging) {
                    setDirty(false);
                    setDragging(userPlayer.hand.dragging);
                    setFirstTile(nearbyTiles[0]);
                    setPlacedLetters(nearbyLetters);
                    setVisibleTiles(nearbyTiles);
                    setPlacedTiles(userPlayer.hand.placed.length);

                    setBiomeTiles(nearbyTiles.map((tile, index) =>
                        <TileBiome key={index} tile={tile}/>
                    ));
                    setBlockTiles(nearbyTiles
                        .filter(tile => tile.place && !tile.letter)
                        .map((tile, index) =>
                            <TileBlock key={index} tile={tile}/>
                        )
                    );
                    setLetterTiles_(nearbyTiles
                        .filter(tile => tile.letter !== undefined)
                        .map((tile, index) =>
                            <TileLetter key={index.toString() + tile.letter?.letter}
                                        game={game} hiddenTilePos={hiddenTilePos}
                                        shovelling={shovelling} tile={tile}/>
                        )
                    );

                    if (nearbyTiles.length > 0) {
                        const centre = nearbyTiles
                            .reduce((s, t) => s.plus(t.position), Vector2.zero)
                            .divide(nearbyTiles.length)
                            .multiply(TILE_SIZE);
                        setContourOrigin(prev => {
                            if (prev === null) return centre;
                            // Only recenter when view has drifted more than half the SVG radius
                            if (Math.abs(centre.x - prev.x) > CONTOUR_RECENTER_X ||
                                Math.abs(centre.y - prev.y) > CONTOUR_RECENTER_Y)
                                return centre;
                            return prev;
                        });
                    }
                }
            }
        };
        const handlePlayerUpdate = () => {
            updateTiles();
        }

        updateTiles();
        userPlayer.events.delegates.add(handlePlayerUpdate);
        return () => userPlayer.events.delegates.remove(handlePlayerUpdate);
    }, [bounds, dirty, game, hiddenTilePos, mapOffset, placedTiles, shovelling, userPlayer, zoom]);
    return (
        <div data-draggable="mapView" style={styles.canvas}>
            <style>{DIG_KEYFRAMES}</style>
            <div style={{ ...layerStyle, zIndex: 10, pointerEvents: "auto" }}>{biomeTiles}</div>
            <div style={{ ...layerStyle, zIndex: 20 }}>
                {contourOrigin && <ContourLines originX={contourOrigin.x} originY={contourOrigin.y} seed={game.seed} />}
            </div>
            <div style={{ ...layerStyle, zIndex: 30 }}>{blockTiles}</div>
            <div style={{ ...layerStyle, zIndex: 40 }}>
                {event?.type === "flood" &&
                    <FloodEvent mapSize={game.size} offset={mapOffset} onComplete={handleEventComplete} tiles={event.tiles}/>
                }
            </div>
            <div style={{ ...layerStyle, zIndex: 40 }}>
                <PlayerLabels game={game} nearbyTiles={visibleTiles} excludeTiles={personalLabelTiles}/>
                <LeaderboardLabels game={game} nearbyTiles={visibleTiles} onPersonalTiles={setPersonalLabelTiles}/>
            </div>
            <div style={{ ...layerStyle, zIndex: 50, pointerEvents: "auto" }}>{letterTiles_}</div>
            <div style={{ ...layerStyle, zIndex: 55 }}>
                {game.placedLegalWords.map((legal, index) =>
                    <WordOutline key={legal.getWordString() + index} legal={true} offset={mapOffset}
                                 scored={scored} word={legal}/>
                )}
                {game.placedIllegalWords.map((legal, index) =>
                    <WordOutline key={index} legal={false} offset={mapOffset} scored={false} word={legal}/>
                )}
            </div>
            <div style={{ ...layerStyle, zIndex: 60, pointerEvents: "none" }}>
                {shovelAnim && (
                    <div key={`${shovelAnim.position.x}-${shovelAnim.position.y}`}
                         style={{
                             position: "absolute",
                             left: Math.floor(shovelAnim.position.x * TILE_SIZE.x),
                             top: Math.floor(shovelAnim.position.y * TILE_SIZE.y),
                             width: TILE_SIZE.x,
                             height: TILE_SIZE.y,
                             ...LETTER_THEME.container,
                             backgroundColor: "white",
                             borderRadius: 8,
                             borderBottom: "slategray solid 3px",
                             borderRight: "slategray solid 3px",
                             display: "flex",
                             alignItems: "center",
                             justifyContent: "center",
                             animation: "dig-animation 900ms ease-in forwards",
                             pointerEvents: "none",
                         }}>
                        <span style={{...LETTER_THEME.content, color: "#000000", pointerEvents: "none"}}>
                            {shovelAnim.letter.letter}
                        </span>
                    </div>
                )}
            </div>
        </div>
    );
}
const mapStateToProps = (store: never) => ({
    map: selectMap(store)
});
const connected = connect(mapStateToProps, {
})(MapView);

export {connected as MapView}
